import React, { useEffect, useState } from "react"
import {
  WingsModal,
  WingsButton,
  WingsTextInput,
  WingsDetailText,
  WingsSelectInput,
} from "@wingspans-ui"
import { FormattedMessage, useIntl } from "react-intl"
import { CareerPlanColors } from "./careerPlanUtils"
import { sendStandardToast } from "../toasts/standard-toast"
import "./UpdateCareerPlanModal.css"

const colorDropdownOptions = Object.entries(CareerPlanColors).map(
  ([key, { label, color }]) => ({
    key: key,
    value: key,
    content: (
      <div className="save-career-plan-color-option w-flex w-flex-row w-items-center w-justify-start w-mr-auto">
        <span
          className="save-career-plan-modal-color-indicator"
          style={{backgroundColor: color}}
        />
        <WingsDetailText>
          {label}
        </WingsDetailText>
      </div>
    ),
    text: (
      <div className="save-career-plan-color-option w-flex w-flex-row w-items-center w-justify-start">
        <span
          className="save-career-plan-modal-color-indicator"
          style={{backgroundColor: color}}
        />
        <WingsDetailText>
          {label}
        </WingsDetailText>
      </div>
    ),
  })
)

const defaultPlan = {
  title: "",
  color: "PINK",
  milestones: [],
}

/**
 * Modal for adding/editing a career plan
 * @param {boolean} show
 * @param {Object} planToEdit - the saved version of the plan being edited
 * @param {Function} onClose
 * @param {Function} onSave - takes in a edited version of the plan to save, or null to delete it. returns an error message if an error occurs
 * @returns {JSX}
 */
const UpdateCareerPlanModal = ({ show, planToEdit, onClose, onSave }) => {
  const { formatMessage } = useIntl()
  const [editedPlan, setEditedPlan] = useState({...defaultPlan})

  const handleShow = () => {
    setEditedPlan({...defaultPlan, ...(planToEdit || {})})
  }

  useEffect(() => {
    if (show) {
      handleShow()
    }
  }, [show, planToEdit])

  const handleSave = async (deleting = false) => {
    const errorMessage = await onSave(deleting ? null : editedPlan)
    if (errorMessage) {
      sendStandardToast({
        heading: `Error when trying to ${deleting ? "delete" : "save"} plan`,
        message: errorMessage,
      })
    } else {
      onClose()
    }
  }

  const handleDelete = async () => {
    await handleSave(true)
  }

  const isNew = !editedPlan.id

  return (
    <WingsModal
      isOpen={show}
      onClose={onClose}
      size={isNew ? "md" : "md"}
      display={isNew ? "display" : "standard"}
      className="update-career-plan-modal"
      titleEyebrow={isNew ? (
        <FormattedMessage id="career-plans-start-from-scratch-modal-eyebrow" defaultMessage="Build a New" />
      ) : undefined}
      title={isNew ? (
        <FormattedMessage id="career-plans-start-from-scratch-modal-heading" defaultMessage="Career Plan" />
      ) : (
        <FormattedMessage id="career-plans-edit-plan" defaultMessage="Edit Career Plan" />
      )}
      titleLevel={isNew ? undefined : 5}
      headerAlign={isNew ? undefined : "left"}
      secondaryAction={isNew ? (
        <WingsButton variant="secondary" size="small" onClick={onClose}>
          <FormattedMessage id="save-career-plan-modal-cancel" defaultMessage="Cancel" />
        </WingsButton>
      ) : (
        <WingsButton
          variant="secondary"
          size="small"
          onClick={handleDelete}
          autoLoading={true}
        >
          <FormattedMessage id="save-career-plan-modal-delete" defaultMessage="Delete" />
        </WingsButton>
      )}
      primaryAction={(
        <WingsButton
          variant="primary"
          size="small"
          onClick={() => handleSave()}
          disabled={!editedPlan.title}
          autoLoading={true}
        >
          <FormattedMessage id="save-career-plan-modal-save" defaultMessage="Save" />
        </WingsButton>
      )}
    >
      <WingsModal.Body className="w-flex w-flex-column w-items-start w-gap-1">
        <WingsTextInput
          label={formatMessage({id: "save-career-plans-modal-title", defaultMessage: "Title"})}
          id="career-plan-title"
          fullWidth={true}
          value={editedPlan.title}
          onChange={(e) => setEditedPlan({...editedPlan, title: e.target.value})}
        />

        <WingsSelectInput
          label={formatMessage({id: "save-career-plans-modal-color", defaultMessage: "Color"})}
          id="career-plan-color-select"
          upward={false}
          fullWidth
          search={false}
          options={colorDropdownOptions}
          value={editedPlan.color}
          onChange={(value) => setEditedPlan({...editedPlan, color: value})}
        />
      </WingsModal.Body>
    </WingsModal>
  )
}

export default UpdateCareerPlanModal
